import React, { useEffect, useRef, useState } from 'react';
import { PageContainer } from '../components/Container/PageContainer';
import { Title, Text, Box, Group, SegmentedControl, Badge, Stack } from '@mantine/core';
import { dbService } from '../firebaseInstance';
import * as d3 from 'd3';
import moment from 'moment';

const AdminVisualization = () => {
	const [users, setUsers] = useState([]);
	const [filter, setFilter] = useState('all');
	const svgRef = useRef(null);
	const containerRef = useRef(null);

	// Filter options for revisit counts
	const filterOptions = [
		{ value: 'all', label: 'All Users' },
		{ value: '0', label: '0 Revisits' },
		{ value: '1-5', label: '1-5 Revisits' },
		{ value: '6-10', label: '6-10 Revisits' },
		{ value: '11+', label: '11+ Revisits' }
	];

	// Helper function to get revisit count for a user
	const getRevisitCount = (user) => {
		return user.visitLog ? user.visitLog.length : 0;
	};

	// Helper function to filter users based on revisit count
	const getFilteredUsers = (users, filterValue) => {
		if (filterValue === 'all') return users;
		
		return users.filter(user => {
			const revisitCount = getRevisitCount(user);
			
			switch (filterValue) {
				case '0':
					return revisitCount === 0;
				case '1-5':
					return revisitCount >= 1 && revisitCount <= 5;
				case '6-10':
					return revisitCount >= 6 && revisitCount <= 10;
				case '11+':
					return revisitCount >= 11;
				default:
					return true;
			}
		});
	};

	useEffect(() => {
		const unsubscribeUsers = dbService
			.collection('user')
			.onSnapshot(async (userSnapshot) => {
				const userData = [];
				userSnapshot.forEach((doc) => {
					const data = doc.data();
					if (data?.profile) {
						userData.push({
							...data,
							docId: doc.id,
						});
					}
				});
				setUsers(userData);
			});

		return () => {
			unsubscribeUsers?.();
		};
	}, []);

	useEffect(() => {
		if (users.length === 0 || !svgRef.current) return;

		// Apply filter to users
		const filteredUsers = getFilteredUsers(users, filter);
		if (filteredUsers.length === 0) return;

		const margin = { top: 40, right: 120, bottom: 80, left: 150 };
		const width = containerRef.current?.offsetWidth || 1200;
		const yUnitHeight = 16;
		const height = Math.max(600, (filteredUsers.length * yUnitHeight) + margin.top + margin.bottom);
		const innerWidth = width - margin.left - margin.right;
		const innerHeight = height - margin.top - margin.bottom;

		d3.select(svgRef.current).selectAll("*").remove();

		const svg = d3.select(svgRef.current)
			.attr("width", width)
			.attr("height", height);

		const g = svg.append("g")
			.attr("transform", `translate(${margin.left},${margin.top})`);

		// Sort filtered users by signup date (newest first)
		const sortedUsers = [...filteredUsers].sort((a, b) => {
			const dateA = a.profile?.signUpDate ? new Date(a.profile.signUpDate) : new Date(0);
			const dateB = b.profile?.signUpDate ? new Date(b.profile.signUpDate) : new Date(0);
			return dateB - dateA; // Newest first
		});

		// Create user index map for data points
		const userIndexMap = {};
		sortedUsers.forEach((user, index) => {
			userIndexMap[user.docId] = index;
		});

		const data = [];
		filteredUsers.forEach((user) => {
			const sortedIndex = userIndexMap[user.docId];
			if (user.profile?.signUpDate) {
				data.push({
					date: new Date(user.profile.signUpDate),
					userIndex: sortedIndex,
					username: user.profile.username,
					type: 'signup',
					userId: user.docId
				});
			}

			if (user.visitLog && Array.isArray(user.visitLog)) {
				user.visitLog.forEach((visitDate) => {
					data.push({
						date: new Date(visitDate),
						userIndex: sortedIndex,
						username: user.profile.username,
						type: 'visit',
						userId: user.docId
					});
				});
			}
		});

		const xScale = d3.scaleTime()
			.domain(d3.extent(data, d => d.date))
			.range([0, innerWidth]);

		const yScale = d3.scaleLinear()
			.domain([0, sortedUsers.length - 1])
			.range([0, sortedUsers.length > 0 ? (sortedUsers.length - 1) * yUnitHeight : 0]);

		const xAxis = d3.axisBottom(xScale)
			.tickFormat(d3.timeFormat("%m-%d-%y"));

		const yAxis = d3.axisLeft(yScale)
			.tickValues(d3.range(sortedUsers.length))
			.tickFormat(i => sortedUsers[i]?.profile?.username || '');

		g.append("g")
			.attr("transform", `translate(0,${innerHeight})`)
			.call(xAxis)
			.selectAll("text")
			.style("text-anchor", "middle");

		g.append("g")
			.call(yAxis);

		g.append("text")
			.attr("transform", `translate(${innerWidth / 2},${innerHeight + margin.bottom - 10})`)
			.style("text-anchor", "middle")
			.text("Time");

		g.append("text")
			.attr("transform", "rotate(-90)")
			.attr("y", 0 - margin.left + 20)
			.attr("x", 0 - (innerHeight / 2))
			.attr("dy", "1em")
			.style("text-anchor", "middle")
			.text("Users");

		const tooltip = d3.select("body").append("div")
			.attr("class", "tooltip")
			.style("position", "absolute")
			.style("visibility", "hidden")
			.style("background", "rgba(0, 0, 0, 0.8)")
			.style("color", "white")
			.style("padding", "8px")
			.style("border-radius", "4px")
			.style("font-size", "12px")
			.style("pointer-events", "none");

		g.selectAll(".dot")
			.data(data)
			.enter().append("circle")
			.attr("class", "dot")
			.attr("cx", d => xScale(d.date))
			.attr("cy", d => yScale(d.userIndex))
			.attr("r", 2)
			.style("fill", d => d.type === 'signup' ? '#1c7ed6' : '#fa5252')
			.style("opacity", 0.7)
			.on("mouseover", function(event, d) {
				tooltip.style("visibility", "visible")
					.text(`${d.username} - ${d.type === 'signup' ? 'Sign Up' : 'Visit'}: ${moment(d.date).format('MM/DD/YYYY HH:mm')}`);
			})
			.on("mousemove", function(event) {
				tooltip.style("top", (event.pageY - 10) + "px")
					.style("left", (event.pageX + 10) + "px");
			})
			.on("mouseout", function() {
				tooltip.style("visibility", "hidden");
			});

		const legend = svg.append("g")
			.attr("transform", `translate(${width - margin.right + 10}, ${margin.top})`);

		const legendData = [
			{ label: "Sign Up", color: "#1c7ed6" },
			{ label: "Visit", color: "#fa5252" }
		];

		const legendItems = legend.selectAll(".legend-item")
			.data(legendData)
			.enter().append("g")
			.attr("class", "legend-item")
			.attr("transform", (d, i) => `translate(0, ${i * 25})`);

		legendItems.append("circle")
			.attr("r", 2)
			.style("fill", d => d.color)
			.style("opacity", 0.7);

		legendItems.append("text")
			.attr("x", 12)
			.attr("y", 0)
			.attr("dy", "0.32em")
			.style("font-size", "12px")
			.text(d => d.label);

		return () => {
			d3.select("body").selectAll(".tooltip").remove();
		};
	}, [users, filter]);

	// Get filtered users and calculate stats for display
	const filteredUsers = getFilteredUsers(users, filter);
	const getFilterStats = () => {
		const stats = {};
		filterOptions.forEach(option => {
			if (option.value === 'all') {
				stats[option.value] = users.length;
			} else {
				stats[option.value] = getFilteredUsers(users, option.value).length;
			}
		});
		return stats;
	};
	const filterStats = getFilterStats();

	return (
		<PageContainer w="100%">
			<Title order={1}>Admin Visualization</Title>
			
			{/* Filter Toolbar */}
			<Stack spacing="md" style={{ marginTop: 20, marginBottom: 20 }}>
				<Group spacing="md" align="center">
					<Text size="sm" fw={500}>Filter by Revisit Count:</Text>
					<SegmentedControl
						value={filter}
						onChange={setFilter}
						data={filterOptions.map(option => ({
							label: `${option.label} (${filterStats[option.value]})`,
							value: option.value
						}))}
						size="sm"
					/>
				</Group>
				
				<Group spacing="xs">
					<Badge variant="light" color="blue" size="sm">
						Showing: {filteredUsers.length} users
					</Badge>
					<Badge variant="light" color="gray" size="sm">
						Total: {users.length} users
					</Badge>
				</Group>
			</Stack>
			
			<Title order={2} fw={300}>
				User Activity Timeline - Filtered Users:{" "}
				<span style={{ fontWeight: "bold" }}>{filteredUsers.length}</span>
			</Title>
			<Box ref={containerRef} style={{ width: '100%', marginTop: 20 }}>
				<svg ref={svgRef}></svg>
			</Box>
		</PageContainer>
	);
};

export default AdminVisualization;